import React from 'react';
import axios, { AxiosResponse } from 'axios';
import { serialize, ClazzOrModelSchema, deserialize } from 'serializr';
import { QueryClient, useQueryClient } from '@tanstack/react-query';

import AIModel, { ModelFiles } from 'types/ai_model';
import { Account } from 'types/account';
import Application from 'types/application';
import { Dashboard, DashboardConfiguration } from 'types/dashboard';
import LumeoJWT from 'types/jwt';
import { Tag } from 'types/tag';
import { Gateway } from 'types/gateway';
import { GatewayConfiguration } from 'types/gateway_configuration';
import { GatewaySpecification } from 'types/gateway_specification';
import { LumeoFile } from 'types/file';
import { OffsetPaginated } from 'types/pagination';

import { handleFileUpload } from 'services/file_upload';
import { REQUEST_HEADERS } from 'services/constants';
import { useAuthentication } from 'hooks/api/useAuthentication';
import errorResponseInterceptor from 'services/api_error_handler';

import { APIObject, API_BASE_URL } from 'services/api';
import { JwtType, JwtParams } from '../../hooks/useJWT';
import { APIContext } from './context';

/** @deprecated */
async function deserializeReq<T>(
  TargetModel: ClazzOrModelSchema<T>,
  req: Promise<AxiosResponse>
): Promise<T | T[] | undefined> {
  const { status, data } = await req;

  if (status >= 400) {
    return;
  }

  if (!data) {
    return data;
  }

  return deserialize(TargetModel, data);
}

async function handleModelFilesUpload(
  model: AIModel,
  { weights_file, metadata_file, labels_file }: ModelFiles
) {
  const promises: Promise<void | Response>[] = [];

  if (model.weights_file_url && weights_file) {
    promises.push(handleFileUpload(model.weights_file_url, weights_file));
  }

  if (model.metadata_file_url && metadata_file) {
    promises.push(handleFileUpload(model.metadata_file_url, metadata_file));
  }

  if (model.labels_file_url && labels_file) {
    promises.push(handleFileUpload(model.labels_file_url, labels_file));
  }

  return Promise.all(promises);
}

function createClient(
  authToken: string | undefined,
  queryClient: QueryClient,
  logout: () => void
) {
  const client = axios.create({
    baseURL: API_BASE_URL,
    responseType: 'json',
    headers: REQUEST_HEADERS,
  });

  if (authToken) {
    client.defaults.headers.common.Authorization = `Bearer ${authToken}`;
  }

  client.interceptors.response.use(
    (res) => res,
    (error) =>
      errorResponseInterceptor(error, queryClient, {
        redirectToLogin: logout,
      })
  );

  return client;
}

export function APIProvider({ children }: React.PropsWithChildren) {
  const queryClient = useQueryClient();
  const { authToken, logout } = useAuthentication();
  const [applicationID, setApplicationID] = React.useState<string>();

  const [client, setClient] = React.useState(() =>
    createClient(authToken, queryClient, logout)
  );

  React.useEffect(() => {
    if (
      !authToken ||
      client.defaults.headers.common.Authorization === `Bearer ${authToken}`
    ) {
      return;
    }

    setClient(() => createClient(authToken, queryClient, logout));
  }, [authToken, client, logout, queryClient]);

  const api = React.useMemo<APIObject>(() => {
    return {
      authToken,
      applicationID,
      setApplicationID,
      client,

      async sendBeacon(url, data) {
        const params = data || new URLSearchParams();
        params.set('access_token', String(authToken));

        return navigator.sendBeacon(
          `${API_BASE_URL}/internal/apps/${applicationID}${url}`,
          params
        );
      },

      /** @deprecated */
      internal: {
        async readServerVersion() {
          return (await client.get('/internal/version')).data;
        },
      },

      applications: {
        async read(applicationID) {
          return deserializeReq(
            Application,
            client.get(`/apps/${applicationID}`)
          ) as Promise<Application>;
        },
        async list() {
          return deserializeReq(Application, client.get('/apps')) as Promise<
            Application[]
          >;
        },
        async create(application) {
          return deserializeReq(
            Application,
            client.post(
              `/orgs/${application.organization_id}/apps`,
              serialize(application)
            )
          ) as Promise<Application>;
        },
        async update(application) {
          return client.put(`/apps/${application.id}`, serialize(application));
        },
        async delete(applicationID) {
          return client.delete(`/apps/${applicationID}`);
        },
        async partnerConfirm(applicationID: string) {
          const { data } = await client.post<{ success: boolean }>(
            `/internal/apps/${applicationID}/partner_confirm`
          );
          return data;
        },
        async ringBind(
          applicationID: string,
          params: { nonce: string; time: number }
        ) {
          const { data } = await client.post<{ success: boolean }>(
            `/internal/apps/${applicationID}/ring/bind`,
            params
          );
          return data;
        },

        gatewayConfiguration: {
          async read(gatewayConfigurationId) {
            const { data } = await client.get<GatewayConfiguration>(
              `/apps/${applicationID}/gateway_configurations/${gatewayConfigurationId}`
            );

            return data;
          },
          async list(params) {
            const { data } = await client.get<
              OffsetPaginated<GatewayConfiguration>
            >(`/apps/${applicationID}/gateway_configurations`, {
              params,
            });

            return data;
          },
          async create(params) {
            const { data } = await client.post(
              `/apps/${applicationID}/gateway_configurations`,
              params
            );

            return data;
          },
          async update({ id, metrics }) {
            const { data } = await client.put(
              `/apps/${applicationID}/gateway_configurations/${id}`,
              { metrics }
            );

            return data;
          },
        },

        dashboards: {
          async create(
            applicationId: string,
            name: string,
            configuration: DashboardConfiguration
          ) {
            const { data } = await client.post<Dashboard>(
              `/internal/apps/${applicationId}/dashboards`,
              { name, configuration }
            );

            return data;
          },
        },
      },

      accessTokens: {
        async list() {
          return (
            await client.get(`/internal/apps/${applicationID}/access_tokens`)
          ).data;
        },
        async create(params) {
          return (
            await client.post(
              `/internal/apps/${applicationID}/access_tokens`,
              params
            )
          ).data;
        },
        async delete(tokenID) {
          return client.delete(
            `/internal/apps/${applicationID}/access_tokens/${tokenID}`
          );
        },
      },

      jwt: {
        async create<Type extends JwtType>(variables: JwtParams<Type>) {
          return deserializeReq(
            LumeoJWT,
            client.post(`/internal/auth/jwt`, variables)
          ) as Promise<LumeoJWT>;
        },
        async createSearchToken() {
          return deserializeReq(
            LumeoJWT,
            client.post(`/apps/${applicationID}/auth/search_jwt`)
          ) as Promise<LumeoJWT>;
        },
        async createInferenceToken() {
          return deserializeReq(
            LumeoJWT,
            client.post(`/apps/${applicationID}/auth/inference_jwt`)
          ) as Promise<LumeoJWT>;
        },
      },

      accounts: {
        async read(accountID) {
          const { data } = await client.get<Account>(
            `/internal/accounts/${accountID}`
          );
          return data;
        },
        async update(account) {
          const { data } = await client.put<Account>(
            `/internal/accounts/${account.id}`,
            account
          );
          return data;
        },
        async updatePassword(accountID, { oldPassword, newPassword }) {
          return client.post(
            `/internal/accounts/${accountID}/update_password`,
            {
              old: oldPassword,
              new: newPassword,
            }
          );
        },
      },

      deployments: {
        addTags(deploymentID, tagIDs) {
          return client.post(
            `/apps/${applicationID}/deployments/${deploymentID}/tags`,
            tagIDs
          ) as Promise<Tag['id'][]>;
        },
        removeTags(deploymentID, tagIDs) {
          return client.delete(
            `/apps/${applicationID}/deployments/${deploymentID}/tags`,
            { params: { tag_ids: tagIDs } }
          ) as Promise<Tag['id'][]>;
        },
      },

      streams: {
        addTags(streamID, tagIDs) {
          return client.post(
            `/apps/${applicationID}/streams/${streamID}/tags`,
            tagIDs
          ) as Promise<Tag['id'][]>;
        },
        removeTags(streamID, tagIDs) {
          return client.delete(
            `/apps/${applicationID}/streams/${streamID}/tags`,
            {
              params: {
                tag_ids: tagIDs,
              },
            }
          ) as Promise<Tag['id'][]>;
        },
      },

      cameras: {
        addTags(cameraID, tagIDs) {
          return client.post(
            `/apps/${applicationID}/cameras/${cameraID}/tags`,
            tagIDs
          ) as Promise<Tag['id'][]>;
        },
        removeTags(cameraID, tagIDs) {
          return client.delete(
            `/apps/${applicationID}/cameras/${cameraID}/tags`,
            { params: { tag_ids: tagIDs } }
          ) as Promise<Tag['id'][]>;
        },
      },

      async snapshot(source) {
        const sourceType = 'stream_type' in source ? 'stream' : 'camera';

        const response = await client.post(
          `/apps/${applicationID}/${sourceType}s/${source.id}/snapshot`,
          {
            gateway_id: source.gateway_id,
          }
        );
        return response.data;
      },

      files: {
        async create(file) {
          const { data } = await client.post(
            `/apps/${applicationID}/files`,
            file
          );

          return data;
        },
        async read(fileId) {
          const { data } = await client.get<LumeoFile>(
            `/apps/${applicationID}/files/${fileId}`
          );

          return data;
        },
        async updateStatus(fileID, cloudStatus) {
          return client.put(
            `/apps/${applicationID}/files/${fileID}/cloud_status`,
            cloudStatus
          );
        },
        addTags(fileId, tagIds) {
          return client.post(
            `/apps/${applicationID}/files/${fileId}/tags`,
            tagIds
          ) as Promise<Tag['id'][]>;
        },
        removeTags(fileId, tagIds) {
          return client.delete(`/apps/${applicationID}/files/${fileId}/tags`, {
            params: { tag_ids: tagIds },
          }) as Promise<Tag['id'][]>;
        },
      },

      gateways: {
        async getSpecification(gatewayID) {
          const { data } = await client.get<GatewaySpecification>(
            `/apps/${applicationID}/gateways/${gatewayID}/specification`
          );

          if (!data) {
            throw new Error('Unable to fetch specification for this gateway.');
          }

          return data;
        },
        addTags(gatewayID, tagIDs) {
          return client.post(
            `/apps/${applicationID}/gateways/${gatewayID}/tags`,
            tagIDs
          ) as Promise<Tag['id'][]>;
        },
        removeTags(gatewayID, tagIDs) {
          return client.delete(
            `/apps/${applicationID}/gateways/${gatewayID}/tags`,
            { params: { tag_ids: tagIDs } }
          ) as Promise<Tag['id'][]>;
        },

        cloud: {
          async create(name, type) {
            const { data } = await client.post<Gateway>(
              `/internal/apps/${applicationID}/cloud_gateways/create`,
              {
                gateway_type: type,
                name,
              }
            );

            return data;
          },
          retry(gatewayID) {
            return client.post(
              `/internal/apps/${applicationID}/gateways/${gatewayID}/retry`
            );
          },
          start(gatewayID) {
            return client.post(
              `/internal/apps/${applicationID}/gateways/${gatewayID}/start`
            );
          },
          stop(gatewayID) {
            return client.post(
              `/internal/apps/${applicationID}/gateways/${gatewayID}/stop`
            );
          },
          restart(gatewayID) {
            return client.post(
              `/apps/${applicationID}/gateways/${gatewayID}/restart`,
              {}
            );
          },
        },
      },

      models: {
        async read(modelID) {
          return deserializeReq(
            AIModel,
            client.get(`/apps/${applicationID}/models/${modelID}`)
          ) as Promise<AIModel>;
        },
        async list() {
          return deserializeReq(
            AIModel,
            client.get(`/apps/${applicationID}/models`)
          ) as Promise<AIModel[]>;
        },
        async create(model, files) {
          model.labels_file_name =
            (files.labels_file as File).name || 'model.labels';

          if (files.metadata_file) {
            model.metadata_file_name = (files.metadata_file as File).name;
          }

          if (files.weights_file) {
            model.weights_file_name = (files.weights_file as File).name;
          }

          const savedModel = (await deserializeReq(
            AIModel,
            client.post(`/apps/${applicationID}/models`, model)
          )) as AIModel;

          await handleModelFilesUpload(savedModel, files);

          return savedModel;
        },
        async update(model, files = {}) {
          const updateModel = { ...model };

          if (files.labels_file) {
            updateModel.labels_file_name = (files.labels_file as File).name;
          }

          if (files.metadata_file) {
            updateModel.metadata_file_name = (files.metadata_file as File).name;
          } else if (files.metadata_file === null) {
            // Delete existing file
            updateModel.metadata_file_name = null;
          } else {
            // Do not request a new upload URL
            delete updateModel.metadata_file_name;
          }

          if (files.weights_file) {
            updateModel.weights_file_name = (files.weights_file as File).name;
          }

          const savedModel = (await deserializeReq(
            AIModel,
            client.put(
              `/apps/${applicationID}/models/${updateModel.id}`,
              updateModel
            )
          )) as AIModel;

          await handleModelFilesUpload(savedModel, files);

          return savedModel;
        },
        async delete(modelID) {
          return client.delete(`/apps/${applicationID}/models/${modelID}`);
        },

        marketplace: {
          async read(modelID) {
            return deserializeReq(
              AIModel,
              client.get(
                `/internal/apps/${applicationID}/marketplace/models/${modelID}`
              )
            ) as Promise<AIModel>;
          },
          async list(params) {
            const response = await client.get<OffsetPaginated<AIModel>>(
              `/internal/apps/${applicationID}/marketplace/models`,
              {
                params: {
                  ...params,
                  pagination: 'offset',
                  page: 1,
                  limit: 150,
                },
              }
            );

            return deserialize(AIModel, response.data.data) as AIModel[];
          },
          admin: {
            async read(modelID) {
              const models = (await deserializeReq(
                AIModel,
                client.get('/internal/marketplace/models')
              )) as AIModel[];
              const model = models.find(({ id }) => id === modelID);

              if (!model) {
                return Promise.reject('Marketplace model not found.');
              }

              return model;
            },
            async list() {
              return deserializeReq(
                AIModel,
                client.get('/internal/marketplace/models')
              ) as Promise<AIModel[]>;
            },
            async create(model, files) {
              const savedModel = (await deserializeReq(
                AIModel,
                client.post('/internal/marketplace/models', model)
              )) as AIModel;

              await handleModelFilesUpload(savedModel, files);

              return savedModel;
            },
            async update(model, files) {
              const updateModel = { ...model };

              if (files.labels_file) {
                updateModel.labels_file_name = (files.labels_file as File).name;
              }

              if (files.metadata_file) {
                updateModel.metadata_file_name = (
                  files.metadata_file as File
                ).name;
              } else if (files.metadata_file === null) {
                // Delete existing file
                updateModel.metadata_file_name = null;
              } else {
                // Do not request a new upload URL
                delete updateModel.metadata_file_name;
              }

              if (files.weights_file) {
                updateModel.weights_file_name = (
                  files.weights_file as File
                ).name;
              }

              const savedModel = (await deserializeReq(
                AIModel,
                client.put(
                  `/internal/marketplace/models/${updateModel.id}`,
                  updateModel
                )
              )) as AIModel;

              await handleModelFilesUpload(savedModel, files);

              return savedModel;
            },
            async delete(modelID) {
              return client.delete(`/internal/marketplace/models/${modelID}`);
            },
          },
        },
      },

      invitations: {
        async accept(variables) {
          return client.post(`/internal/invitations/accept`, variables);
        },
        async acceptAndCreateAccount(variables) {
          return client.post(
            `/internal/invitations/accept_and_create_account`,
            variables
          );
        },
        async decline(variables) {
          return client.post(`/internal/invitations/decline`, variables);
        },
        async hasAccount(token: string) {
          return client.get(`/internal/invitations/has_account?token=${token}`);
        },
      },

      webrtc: {
        async getOffer(streamID) {
          const { data } = await client.get(
            `/apps/${applicationID}/streams/${streamID}/webrtc/get_offer`
          );
          return data;
        },

        async close(streamID, peerID) {
          const { data } = await client.get(
            `/apps/${applicationID}/streams/${streamID}/webrtc/${peerID}/hang_up`
          );
          return data;
        },

        async setAnswer(streamID, peerID, data) {
          return client.post(
            `/apps/${applicationID}/streams/${streamID}/webrtc/${peerID}/set_answer`,
            data
          );
        },

        async getCandidates(streamID, peerID) {
          const { data } = await client.get(
            `/apps/${applicationID}/streams/${streamID}/webrtc/${peerID}/get_candidates`
          );
          return data;
        },

        async addCandidate(streamID, peerID, candidate) {
          const { data } = await client.post(
            `/apps/${applicationID}/streams/${streamID}/webrtc/${peerID}/add_candidate`,
            candidate.toJSON()
          );
          return data;
        },

        async whepConnect(streamID, offerSdp, signal) {
          const response = await client.post(
            `/apps/${applicationID}/streams/${streamID}/whep`,
            offerSdp,
            {
              headers: {
                'Content-Type': 'application/sdp',
                Accept: 'application/sdp',
              },
              // Tell Axios to treat response as plain text
              responseType: 'text',
              // Allow the request to be cancelled
              signal,
            }
          );

          // Extract session ID from Location header
          const location = response.headers['location'] || '';
          const sessionId = location.split('/').pop() || '';

          return {
            sessionId,
            answerSdp: response.data,
          };
        },

        async whepDisconnect(streamID: string, sessionId: string) {
          await client.delete(
            `/apps/${applicationID}/streams/${streamID}/whep/${sessionId}`
          );
        },

        async whepPatch(streamID: string, sessionId: string, sdpfrag: string) {
          await client.patch(
            `/apps/${applicationID}/streams/${streamID}/whep/${sessionId}`,
            sdpfrag,
            {
              headers: {
                'Content-Type': 'application/trickle-ice-sdpfrag',
              },
            }
          );
        },
      },
    };
  }, [client, authToken, applicationID]);

  return <APIContext.Provider value={api}>{children}</APIContext.Provider>;
}
